import { CheckOutlined, DeleteOutlined, EditOutlined, RollbackOutlined, SendOutlined } from "@ant-design/icons";
import { usePureEffect } from "@buzzeasy/shared-frontend-utilities";
import { Button, Input } from "antd";
import { TextAreaRef } from "antd/lib/input/TextArea";
import React, { ReactElement, useRef } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { InputContainerProps, InputInterfaceRef } from "../containers/InputContainer";
import IconButton from "./IconButton";
import InputAddons from "./InputAddons";
import Countdown from "./TranscriptConfirmAndEdit.Countdown";

export interface TranscriptConfirmAndEditProps extends InputContainerProps {
  isEditing: boolean;
  isAgentSpeaking: boolean;
  projectedSendDate: Date | null;
  sendTimeoutSeconds: number;
  text: string;
  onTextChange(text: string): void;
  onSend(): void;
  onClear(): void;
  onSwitchToEditMode(): void;
  onSwitchToVoiceMode(): void;
}

export default function TranscriptConfirmAndEdit(props: TranscriptConfirmAndEditProps): ReactElement {
  const {
    componentRef,
    showQuickResponses,
    showSuggestedAnswer,
    onShowQuickResponses,
    onShowSuggestedAnswer,
    isEditing,
    isAgentSpeaking,
    projectedSendDate,
    sendTimeoutSeconds,
    text,
    onTextChange,
    onSend,
    onClear,
    onSwitchToEditMode,
    onSwitchToVoiceMode,
  } = props;

  const { t } = useTranslation();

  const inputRef = useRef<TextAreaRef>(null);

  usePureEffect(
    (refs) => {
      const input = inputRef.current?.resizableTextArea?.textArea;
      if (!refs.isEditing && input)
        input.scrollTop = input.scrollHeight;
    },
    [text],
    { isEditing },
  );

  const handleSend = () => {
    if (text.trim() === "")
      return;

    onSend();
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (isEditing && e.key === "Enter" && !e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey) { // if enter is pressed without any modifiers
      handleSend();
      e.preventDefault();
    }
  };

  const handleSwitchToEditMode = () => {
    onSwitchToEditMode();
    inputRef.current?.focus();

    const textArea = inputRef.current?.resizableTextArea?.textArea;

    if (textArea)
      textArea.setSelectionRange(textArea.value.length, textArea.value.length);
  };

  if (componentRef) {
    (componentRef as React.MutableRefObject<InputInterfaceRef>).current = {
      setInputTextAndFocus: () => inputRef.current?.focus(),
    };
  }

  const inputPlaceholder = isEditing
    ? t("input.inputPlaceholder")
    : isAgentSpeaking
      ? t("input.listeningHint")
      : t("input.inputPlaceholderVoice");

  return (
    <RootBox>
      <InputAddons
        isEditingTranscript={isEditing}
        isAgentSpeaking={isAgentSpeaking}
        showQuickResponses={showQuickResponses}
        showSuggestedAnswer={showSuggestedAnswer}
        onShowQuickResponses={onShowQuickResponses}
        onShowSuggestedAnswer={onShowSuggestedAnswer}
      />
      <InputAndActionsBox>
        {
          text &&
          <MainActionsBox>
            <Button size="small" type="link" icon={<DeleteOutlined />} onClick={onClear}>
              {t("input.clear")}
            </Button>
            {
              isEditing
                ? <Button size="small" type="link" icon={<CheckOutlined />} onClick={onSwitchToVoiceMode}>
                    {t("input.finishEditing")}
                  </Button>
                : <Button size="small" type="link" icon={<EditOutlined />} onClick={handleSwitchToEditMode}>
                    {t("input.edit")}
                  </Button>
            }
          </MainActionsBox>
        }
        <InputOverlayBox>
          <StyledTextArea
            ref={inputRef}
            readOnly={!isEditing}
            value={text}
            onChange={e => onTextChange(e.target.value)}
            onKeyDown={handleKeyDown}
            autoSize={{ minRows: 1, maxRows: 5 }}
            placeholder={inputPlaceholder}
            tabIndex={isEditing ? 0 : -1}
          />
          {
            (!isEditing && !text) &&
            <OverlayButton icon={<EditOutlined />} onClick={handleSwitchToEditMode}>
              {t("input.switchToTyping")}
            </OverlayButton>
          }
        </InputOverlayBox>
      </InputAndActionsBox>
      <QuickActionsBox $direction={text ? "column" : "row"}>
        {
          isEditing
            ? !text
                ? <IconButton
                    type="link"
                    title={t("input.cancelEditing")}
                    icon={<RollbackOutlined />}
                    onClick={onSwitchToVoiceMode}
                  />
                : <Countdown
                    endDate={projectedSendDate}
                    timeoutSeconds={sendTimeoutSeconds}
                  />
            : text
              ? <Countdown
                  endDate={projectedSendDate}
                  timeoutSeconds={sendTimeoutSeconds}
                />
              : null
        }
        <IconButton
          type="link"
          disabled={text.trim() === ""}
          title={t("input.send")}
          icon={<SendOutlined />}
          onClick={handleSend}
          style={{ fontSize: "1.1em" }}
        />
      </QuickActionsBox>
    </RootBox>
  );
}

const RootBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: end;
  gap: ${p => p.theme.paddingXXS}px;

  & > * {
    flex: 0 0 auto;
  }

  & > :nth-child(2) {
    flex: 1 1 auto;
  }
`;

const InputAndActionsBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${p => p.theme.paddingXXS}px;
`;

const MainActionsBox = styled.div`
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  gap: ${p => p.theme.paddingXS}px;
  flex-wrap: wrap;
`;

const QuickActionsBox = styled.div<{ $direction: "row" | "column" }>`
  display: flex;
  flex-direction: ${p => p.$direction};
  justify-content: center;
  align-items: center;
`;

const StyledTextArea = styled(Input.TextArea)`
  background-color: ${p => p.readOnly && p.theme.colorBgLayout} !important;
  color: ${p => p.readOnly && p.theme.colorTextSecondary} !important;

  &:hover {
    border-color: ${p => p.readOnly && p.theme.colorPrimaryBg} !important;
  }
`;

const InputOverlayBox = styled.div`
  position: relative;

  &:focus-within { // to support keyboard navigation
    .ct-OverlayButton {
      opacity: 1;
    }
  }
`;

const OverlayButton = styled(Button).attrs({ className: "ct-OverlayButton", type: "link" })`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  
  color: ${p => p.theme.colorPrimary} !important;
  background-color: ${p => p.theme.colorBgLayout} !important;
  border-radius: ${p => p.theme.borderRadius}px;
  border: ${p => p.theme.lineWidth}px ${p => p.theme.lineType} ${p => p.theme.colorBorder};

  opacity: 0;

  &:hover {
    opacity: 1;
  }

  transition: all ${p => p.theme.motionDurationMid} ${p => p.theme.motionEaseInOut};
`;
